import { type ApolloClient } from "@apollo/client";
import { type Aperture, type ComponentEffect, toRender } from "refract-rxjs";
import { type ObservableInput, combineLatest } from "rxjs";
import { expand, filter, map, startWith } from "rxjs/operators";

import { type GetTopLensesQuery } from "../api/queries";
import { type TopLensCardState } from "../models";

import { createTopLensCardState, defaultTopLensCardState } from "./createTopLensCardState";
import { getLensDetails } from "./getLensDetails";

export interface GetLensInfoCardsInput<TLoad> {
    topLensesStats: GetTopLensesQuery | undefined;
    topLensesStatsLoading: boolean;
    client: TLoad;
}

export interface GetLensInfoCardsOutput {
    topLensesCards: TopLensCardState;
}

export const getTopLensesCardsEffect: Aperture<
    GetLensInfoCardsInput<ApolloClient<unknown>>,
    ComponentEffect<GetLensInfoCardsOutput>
> = ({ observe }, { client }) => {
    const topLensesStats$ = observe<GetTopLensesQuery | undefined>("topLensesStats");
    const topLensesStatsLoading$ = observe<boolean>("topLensesStatsLoading");

    return combineLatest([topLensesStatsLoading$, topLensesStats$]).pipe(
        filter((values): values is [boolean, GetTopLensesQuery] => {
            const [loading, data] = values;
            return !loading && data !== undefined;
        }),
        map<[boolean, GetTopLensesQuery], TopLensCardState>(([, topLenses]) => createTopLensCardState(topLenses)),
        expand<TopLensCardState, ObservableInput<TopLensCardState>>((state) =>
            getLensDetails(client, topLensesStats$, state),
        ),
        startWith(defaultTopLensCardState),
        map((topLensesCards) => toRender<GetLensInfoCardsOutput>({ topLensesCards })),
    );
};
